import { useState, useEffect } from 'react';
import { apiService } from '../services/apiService';

const SESSION_STORAGE_KEY = 'subscription_data';
const SESSION_STORAGE_DISMISSED_KEY = 'subscription_bar_dismissed';
const CACHE_DURATION = 30 * 1000; // 30 seconds cache duration

// Module-level state to share across all hook instances
let listeners = [];
let dismissedListeners = [];
let isFetching = false;

// Helper functions for session storage
const getFromSessionStorage = () => {
    try {
        const cached = sessionStorage.getItem(SESSION_STORAGE_KEY);
        if (!cached) return null;

        const { data, timestamp } = JSON.parse(cached);
        const now = Date.now();

        // Check if cache is still valid
        if (now - timestamp > CACHE_DURATION) {
            sessionStorage.removeItem(SESSION_STORAGE_KEY);
            return null;
        }

        return data;
    } catch {
        return null;
    }
};

const saveToSessionStorage = (data) => {
    try {
        sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify({
            data,
            timestamp: Date.now()
        }));
    } catch (error) {
        console.error('Failed to save to session storage:', error);
    }
};

const fetchSubscriptionStatus = () => {
    isFetching = true;

    return apiService.apiGet(import.meta.env.VITE_API_URL + '/subscribe/getCurrentSubscription')
        .then((data) => {
            
            const subscriptionInfo = {
                status: data?.status || null,
                trialEnd: data?.trial_end || null,
                hasPaymentMethod: data?.default_payment_method != null || data?.has_payment_method === true,
                cancelAtPeriodEnd: data?.cancel_at_period_end || false
            };

            saveToSessionStorage(subscriptionInfo);

            // Update all listeners
            listeners.forEach(listener => listener(subscriptionInfo));
            isFetching = false;
            return subscriptionInfo;
        })
        .catch(() => {
           const subscriptionInfo = {
                status: null,
               
            };
            saveToSessionStorage(subscriptionInfo);

            // Update all listeners
            listeners.forEach(listener => listener(subscriptionInfo));
            isFetching = false;
        });
};

// Callable from outside React (e.g. after login) to populate the cache eagerly
export const prefetchSubscriptionStatus = () => {
    if (isFetching) return;
    return fetchSubscriptionStatus();
};

// Callable from outside React to force a re-fetch and update all hook instances
export const refreshSubscriptionStatus = () => {
    sessionStorage.removeItem(SESSION_STORAGE_KEY);
    return fetchSubscriptionStatus();
};

// Callable from outside React to un-dismiss the notification bar so it shows again
export const showNotificationBar = () => {
    sessionStorage.removeItem(SESSION_STORAGE_DISMISSED_KEY);
    dismissedListeners.forEach(listener => listener(false));
};

export const useSubscriptionStatus = () => {
    const [subscriptionData, setSubscriptionData] = useState(() => getFromSessionStorage());
    const [isNotificationDismissed, setIsNotificationDismissed] = useState(
        () => typeof sessionStorage !== 'undefined' && sessionStorage.getItem(SESSION_STORAGE_DISMISSED_KEY) === 'true'
    );

    useEffect(() => {
        // Add this component to listeners
        listeners.push(setSubscriptionData);
        dismissedListeners.push(setIsNotificationDismissed);

        const cachedData = getFromSessionStorage();

        // Fetch if not cached and not currently fetching
        if (cachedData === null && !isFetching) {
            fetchSubscriptionStatus();
        } else if (cachedData !== null) {
            // Use cached value immediately
            setSubscriptionData(cachedData);
        }

        // Cleanup: remove listeners when component unmounts
        return () => {
            listeners = listeners.filter(listener => listener !== setSubscriptionData);
            dismissedListeners = dismissedListeners.filter(listener => listener !== setIsNotificationDismissed);
        };
    }, []);

    const refreshSubscriptionStatus = () => {
        fetchSubscriptionStatus();
    };

    const clearCache = () => {
        sessionStorage.removeItem(SESSION_STORAGE_KEY);
        setSubscriptionData(null);
        listeners.forEach(listener => listener(null));
    };

    const dismissNotificationBar = () => {
        sessionStorage.setItem(SESSION_STORAGE_DISMISSED_KEY, 'true');
        // Update all listeners to hide the notification bar immediately
        dismissedListeners.forEach(listener => listener(true));
    };

    // TEMPORARY: forced true so trial users are never blurred and the
    // subscription bar never shows, regardless of actual conversion status.
    // Revert to the real check below once we want that gating back:
    // const willTrialConvert = subscriptionData?.status === 'trialing'
    //     && subscriptionData?.hasPaymentMethod
    //     && !subscriptionData?.cancelAtPeriodEnd;
    const willTrialConvert = true;

    return {
        subscriptionData,
        status: subscriptionData?.status,
        trialEnd: subscriptionData?.trialEnd,
        hasPaymentMethod: subscriptionData?.hasPaymentMethod,
        cancelAtPeriodEnd: subscriptionData?.cancelAtPeriodEnd,
        willTrialConvert,
        isNotificationDismissed,
        refreshSubscriptionStatus,
        dismissNotificationBar,
        clearCache
    };
};
